import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Settings } from '@thebb/shared';
import { get, put } from '../api';
import { ErrorBox, PageTitle, Spinner } from '../components/ui';

export function SettingsPage() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['settings'], queryFn: () => get<Settings>('/settings') });
  const [form, setForm] = useState<Settings | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (data.data) setForm(data.data);
  }, [data.data]);
  const save = useMutation({ mutationFn: () => put('/settings', form), onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }), onError: setError });

  if (!form) return <Spinner />;
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setForm({ ...form, [k]: v });

  return (
    <div className="max-w-xl">
      <PageTitle
        title="Indstillinger"
        actions={
          <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Gem
          </button>
        }
      />
      <ErrorBox error={error} />
      <div className="card space-y-3">
        <div>
          <label className="label">Firmanavn</label>
          <input className="input" value={form['company.name']} onChange={(e) => set('company.name', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Standardsprog</label>
            <select className="input" value={form['company.locale']} onChange={(e) => set('company.locale', e.target.value as 'da' | 'en')}>
              <option value="da">Dansk</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="label">Tidszone</label>
            <input className="input" value={form['company.timezone']} onChange={(e) => set('company.timezone', e.target.value)} />
          </div>
        </div>
        <h2 className="pt-2 text-sm font-semibold">Sikkerhed</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Session (timer)</label>
            <input className="input" type="number" min={1} value={form['security.sessionHours']} onChange={(e) => set('security.sessionHours', Number(e.target.value))} />
          </div>
          <label className="flex items-center gap-2 pt-5 text-sm">
            <input type="checkbox" checked={form['security.requireTotp']} onChange={(e) => set('security.requireTotp', e.target.checked)} /> Kræv 2FA for alle
          </label>
        </div>
        <h2 className="pt-2 text-sm font-semibold">Print</h2>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Agent offline efter (sek.)</label>
            <input className="input" type="number" min={60} value={form['printing.agentOfflineAfterSeconds']} onChange={(e) => set('printing.agentOfflineAfterSeconds', Number(e.target.value))} />
          </div>
          <div>
            <label className="label">Job-lease (sek.)</label>
            <input className="input" type="number" min={10} value={form['printing.jobLeaseSeconds']} onChange={(e) => set('printing.jobLeaseSeconds', Number(e.target.value))} />
          </div>
          <div>
            <label className="label">Maks. forsøg</label>
            <input className="input" type="number" min={1} value={form['printing.maxAttempts']} onChange={(e) => set('printing.maxAttempts', Number(e.target.value))} />
          </div>
        </div>
      </div>
    </div>
  );
}
